import React from 'react';
import { Link } from 'react-router-dom';
import { Snowflake, Wind, Wrench, ShoppingBag, ArrowRight, CheckCircle, Store, Package, WashingMachine, Shirt, Refrigerator, CookingPot, Sparkles, Thermometer, SprayCan, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';

const services = [
  {
    id: 'climatisation-chauffage',
    icon: Snowflake,
    title: 'Climatisation & Chauffage',
    description: 'Solutions complètes pour votre confort toute l\'année. Installation, entretien et optimisation de vos systèmes de climatisation et chauffage.',
    features: [
      'Installation de systèmes muraux et centraux',
      'Nettoyage de systèmes muraux et centraux',
      'Optimisation énergétique',
      'Diagnostic complet',
      'Installation de fournaises',
      'Entretien préventif annuel ou saisonnier'
    ],
    color: 'from-blue-500 to-orange-500',
    bgColor: 'bg-blue-50',
    image: 'https://images.pexels.com/photos/7535070/pexels-photo-7535070.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
  },
  {
    id: 'nettoyage',
    icon: SprayCan,
    title: 'Nettoyage',
    description: 'Service de nettoyage professionnel pour maintenir vos équipements en parfait état et prolonger leur durée de vie.',
    features: [
      'Nettoyage d\'échangeur d\'air',
      'Nettoyage de thermopompe centrale',
      'Nettoyage de thermopompe murale'
    ],
    color: 'from-cyan-500 to-blue-500',
    bgColor: 'bg-cyan-50',
    image: '/assets/nettoyage-le-bleu-des-champs.png'
  },
  {
    id: 'ventilation',
    icon: Wind,
    title: 'Ventilation',
    description: 'Améliorez la qualité de l\'air de votre maison avec nos solutions de ventilation adaptées à vos besoins.',
    features: [
      'Humidificateur',
      'Échangeurs d\'air',
      'Nettoyage de conduits de ventilation',
      'Déshumidificateur'
    ],
    color: 'from-teal-500 to-green-500',
    bgColor: 'bg-teal-50',
    image: 'https://images.pexels.com/photos/11538226/pexels-photo-11538226.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
  },
  {
    id: 'reparation-electromenagers',
    icon: Wrench,
    title: 'Réparation d\'électroménagers',
    description: 'Service de réparation professionnel pour tous vos appareils électroménagers. Toutes marques confondues.',
    features: [
      'Laveuse',
      'Sécheuse',
      'Lave-vaisselle',
      'Cuisinière',
      'Réfrigérateur',
      'Congélateurs'
    ],
    color: 'from-purple-500 to-indigo-500',
    bgColor: 'bg-purple-50',
    image: 'https://images.pexels.com/photos/7614540/pexels-photo-7614540.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
  },
  {
    id: 'vente-pieces',
    icon: ShoppingBag,
    title: 'Électroménagers usagés & Pièces',
    description: 'Visitez notre magasin pour des électroménagers usagés de qualité et notre comptoir de pièces pour toutes vos réparations.',
    features: [],
    hasCustomContent: true,
    color: 'from-amber-500 to-yellow-500',
    bgColor: 'bg-slate-800',
    image: '/assets/pieces-le-bleu-des-champs.png'
  }
];

const usedAppliances = [
  { name: 'Laveuse', Icon: WashingMachine },
  { name: 'Sécheuse', Icon: Shirt },
  { name: 'Frigo', Icon: Refrigerator },
  { name: 'Cuisinière', Icon: CookingPot },
  { name: 'Lave-vaiss.', Icon: Sparkles },
  { name: 'Congélo', Icon: Thermometer }
];

export const ServicesPage = () => {
  return (
    <div>
      {/* Hero */}
      <section className="hero-gradient py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-20 w-72 h-72 bg-[#00b4d8] rounded-full blur-3xl"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Nos Services
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            Une gamme complète de services pour votre confort résidentiel et commercial
          </p>
        </div>
      </section>

      {/* Services List */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-32">
            {services.map((service, index) => {
              const Icon = service.icon;
              const isEven = index % 2 === 0;
              
              return (
                <div 
                  key={service.id}
                  id={service.id}
                  className={`grid lg:grid-cols-2 gap-12 items-center ${!isEven ? 'lg:flex-row-reverse' : ''}`}
                  data-testid={`service-section-${service.id}`}
                >
                  <div className={`${!isEven ? 'lg:order-2' : ''}`}>
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${service.color} flex items-center justify-center mb-6`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-[#1a3a5c] mb-4">
                      {service.title}
                    </h2>
                    <p className="text-slate-600 text-lg mb-8">
                      {service.description}
                    </p>
                    
                    {/* Custom content for Vente & Pièces */}
                    {service.hasCustomContent ? (
                      <div className="space-y-6">
                        {/* Électroménagers usagés */}
                        <div className="bg-white border border-slate-200 rounded-2xl p-6">
                          <div className="flex items-center gap-3 mb-4">
                            <Store className="w-6 h-6 text-[#00b4d8]" />
                            <h3 className="text-xl font-semibold text-[#1a3a5c]">Électroménagers usagés</h3>
                          </div>
                          <p className="text-slate-600 mb-4">En magasin à Saint-Césaire</p>
                          <div className="grid grid-cols-6 gap-2 mb-4">
                            {usedAppliances.map((appliance, idx) => {
                              const ApplianceIcon = appliance.Icon;
                              return (
                                <div key={idx} className="text-center p-2 bg-slate-50 rounded-xl hover:bg-[#00b4d8]/10 transition-colors">
                                  <div className="w-8 h-8 mx-auto rounded-full bg-[#00b4d8]/10 flex items-center justify-center mb-1">
                                    <ApplianceIcon className="w-4 h-4 text-[#00b4d8]" />
                                  </div>
                                  <p className="text-[10px] text-slate-600 font-medium leading-tight">{appliance.name}</p>
                                </div>
                              );
                            })}
                          </div>
                          <a href="tel:+14504693483" className="inline-flex items-center gap-2 text-sm text-[#00b4d8] font-medium hover:underline">
                            <Phone className="w-4 h-4" />
                            Contactez-nous pour les disponibilités
                          </a>
                        </div>
                        
                        {/* Comptoir de pièces */}
                        <div className="bg-white border border-slate-200 rounded-2xl p-6">
                          <div className="flex items-center gap-3 mb-4">
                            <Package className="w-6 h-6 text-[#00b4d8]" />
                            <h3 className="text-xl font-semibold text-[#1a3a5c]">Comptoir de pièces</h3>
                          </div>
                          <p className="text-slate-600 mb-4">
                            Commandez vos pièces d'électroménagers. Toutes marques disponibles.
                          </p>
                          <Link to="/commande-piece">
                            <Button 
                              className="rounded-full bg-[#1a3a5c] hover:bg-[#0f2a4a] text-white px-6"
                              data-testid="order-part-btn"
                            >
                              Commander une pièce
                              <ArrowRight className="ml-2 w-4 h-4" />
                            </Button>
                          </Link>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="grid sm:grid-cols-2 gap-3 mb-8">
                          {service.features.map((feature, idx) => (
                            <div key={idx} className="flex items-center gap-3">
                              <CheckCircle className="w-5 h-5 text-[#00b4d8] flex-shrink-0" />
                              <span className="text-slate-600">{feature}</span>
                            </div>
                          ))}
                        </div>
                        {service.id === 'nettoyage' ? (
                          <Link to="/demande-nettoyage">
                            <Button 
                              className="rounded-full bg-[#00b4d8] hover:bg-[#009bb8] text-white px-8"
                              data-testid={`cleaning-btn-${service.id}`}
                            >
                              Faire une demande de nettoyage
                              <ArrowRight className="ml-2 w-4 h-4" />
                            </Button>
                          </Link>
                        ) : service.id === 'reparation-electromenagers' ? (
                          <a href="tel:+14504693483">
                            <Button 
                              className="rounded-full bg-[#00b4d8] hover:bg-[#009bb8] text-white px-8"
                              data-testid={`phone-btn-${service.id}`}
                            >
                              <Phone className="mr-2 w-4 h-4" />
                              Prendre un rendez-vous
                            </Button>
                          </a>
                        ) : (
                          <Link to="/soumission">
                            <Button 
                              className="rounded-full bg-[#00b4d8] hover:bg-[#009bb8] text-white px-8"
                              data-testid={`quote-btn-${service.id}`}
                            >
                              Demander une soumission
                              <ArrowRight className="ml-2 w-4 h-4" />
                            </Button>
                          </Link>
                        )}
                      </>
                    )}
                  </div>

                  <div className={`${!isEven ? 'lg:order-1' : ''}`}>
                    <div className={`${service.bgColor} rounded-3xl p-4 lg:p-6 relative overflow-hidden`}>
                      <img 
                        src={service.image} 
                        alt={service.title}
                        className="w-full h-64 lg:h-80 object-cover rounded-2xl shadow-lg"
                      />
                      {service.hasBlueOverlay && (
                        <div className="absolute inset-4 lg:inset-6 rounded-2xl bg-gradient-to-br from-[#00b4d8]/40 to-[#1a3a5c]/50 mix-blend-overlay"></div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-[#1a3a5c] mb-6">
            Prêt à améliorer votre confort?
          </h2>
          <p className="text-slate-600 text-lg mb-8">
            Contactez-nous dès aujourd'hui pour une consultation gratuite et un devis personnalisé.
          </p>
          <div className="flex justify-center">
            <Link to="/soumission">
              <Button 
                size="lg" 
                className="rounded-full bg-[#00b4d8] hover:bg-[#009bb8] text-white px-8"
              >
                Obtenir une soumission
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
